/**
 * Tour manager — the singleton that ties everything together.
 *
 * - Holds the registry of known tours.
 * - Resolves per-tour settings from the active environment's `config/tour.config.<env>.ts`.
 * - Decides whether a tour may auto-start (enabled ∧ autoStart ∧ in schedule window
 *   ∧ not already seen ∧ not in View-As).
 * - Starts/stops the driver.js engine and records "seen" on finish or skip.
 * - Supports `?tour=<id>` force-launch for QA/design review.
 *
 * Host apps wire it once (see `useTour`/App bootstrap): register the tours, provide an
 * `isViewAs` predicate, then call `autoStart(id)` from the screen that hosts each tour.
 */

import { activeConfig } from '../config';
import { resolveEnv } from './env';
import { resolveSettings, withinSchedule, type ResolvedSettings } from './settings';
import { localStorageSeenStore, type SeenStore } from './seenStore';
import { createTourEngine, type TourEngine } from './tourEngine';
import type { TourDefinition } from './types';

export interface TourManagerInit {
  /** Return true while an admin is "viewing as" a learner — suppresses auto-start. */
  isViewAs?: () => boolean;
  /**
   * Return true when something else should hold auto-start back right now — e.g. a
   * blocking modal is open. App-specific (the app knows its own modal system).
   */
  blocked?: () => boolean;
  /** Override the persistence layer (defaults to localStorage). */
  seenStore?: SeenStore;
  /** Optional analytics/debug hook for lifecycle events. */
  onEvent?: (event: TourEventName, id: string) => void;
}

/**
 * Lifecycle events surfaced to the host via `onEvent`.
 * - `start` / `complete` / `skip` — the tour actually ran.
 * - `abort` — the tour was asked to auto-start but will NOT run (a give-up path:
 *   ineligible, disabled, or the defer-until-unblocked window ran out). Hosts that
 *   gate something on the tour (e.g. a modal-queue slot) must treat `abort` as a
 *   terminal signal just like `complete`/`skip`, or the tour can hold that resource
 *   forever. Contract: every `autoStart(id)` yields exactly one terminal event —
 *   `complete`/`skip` (it ran) or `abort` (it didn't).
 */
export type TourEventName = 'start' | 'complete' | 'skip' | 'abort';

/** Sentinel: the only *transient* block — everything else is eligible, just wait it out. */
const BLOCKED_REASON = 'blocked (a modal or overlay is open)';
/** Give up deferring after this long so we never poll forever. */
const DEFER_TIMEOUT_MS = 120000;
const DEFER_POLL_MS = 400;

class TourManager {
  private tours = new Map<string, TourDefinition>();
  private isViewAs: () => boolean = () => false;
  private blocked: () => boolean = () => false;
  private seen: SeenStore = localStorageSeenStore;
  private onEvent?: (event: TourEventName, id: string) => void;
  private active: TourEngine | null = null;
  private activeId: string | null = null;
  private deferTimer: number | null = null;
  private activeMonitor: number | null = null;
  private activeObserver: MutationObserver | null = null;

  init(options: TourManagerInit = {}): void {
    if (options.isViewAs) this.isViewAs = options.isViewAs;
    if (options.blocked) this.blocked = options.blocked;
    if (options.seenStore) this.seen = options.seenStore;
    if (options.onEvent) this.onEvent = options.onEvent;
  }

  register(definitions: TourDefinition[]): void {
    definitions.forEach((def) => this.tours.set(def.id, def));
  }

  /** Resolve a tour's settings from the active environment's config file. */
  private settingsFor(id: string): ResolvedSettings {
    return resolveSettings(activeConfig()[id]);
  }

  isEnabled(id: string): boolean {
    return this.settingsFor(id).enabled;
  }

  /** Whether a tour is allowed to launch itself right now. */
  shouldAutoStart(id: string): boolean {
    return this.autoStartBlockReason(id) === null;
  }

  /**
   * Like shouldAutoStart but ignores the transient "a modal/overlay is open" block.
   * Used as the modal queue's `canShow`: the queue cannot re-pump when an unrelated
   * dialog closes, so gating on the transient block there could make the tour skip
   * for the whole session. Once the tour becomes the current queue item, autoStart()
   * re-applies the transient block and defers-until-unblocked as needed.
   */
  isAutoStartEligible(id: string): boolean {
    return this.autoStartBlockReason(id, { skipTransientBlock: true }) === null;
  }

  /**
   * Returns why a tour can't auto-start, or null if it can. Permanent gates are checked
   * first; the transient BLOCKED_REASON (a modal is open) is last, so it can be told apart
   * from the "give up" reasons and deferred instead. Pass `skipTransientBlock` to test
   * only the permanent gates.
   */
  private autoStartBlockReason(id: string, { skipTransientBlock = false } = {}): string | null {
    const def = this.tours.get(id);
    if (!def) return 'not registered';
    const settings = this.settingsFor(id);
    if (!settings.enabled) return `disabled in this environment (${resolveEnv()})`;
    if (!settings.autoStart) return 'autoStart is false for this environment';
    if (!withinSchedule(settings, new Date())) return 'outside its schedule window';
    if (this.isViewAs()) return 'suppressed during View-As';
    if (this.seen.hasSeen(id, def.version)) {
      return `already seen (run localStorage.removeItem('asu.tour.${id}') to replay)`;
    }
    if (!skipTransientBlock && this.blocked()) return BLOCKED_REASON;
    return null;
  }

  /** Call from the mounted hook of the screen that owns the tour. */
  autoStart(id: string): void {
    const reason = this.autoStartBlockReason(id);
    if (reason === null) {
      // eslint-disable-next-line no-console
      console.info(`[tour] "${id}" auto-starting`);
      this.start(id);
      return;
    }
    if (reason === BLOCKED_REASON) {
      // Eligible except a modal is open — wait for it to close, then start.
      this.deferUntilUnblocked(id);
      return;
    }
    // eslint-disable-next-line no-console
    console.info(`[tour] "${id}" did not auto-start: ${reason}`);
    // Terminal give-up: signal so a host gating on the tour (modal-queue slot) frees it.
    this.onEvent?.('abort', id);
  }

  /** Poll until the blocking overlay clears (or we give up / become ineligible), then start. */
  private deferUntilUnblocked(id: string): void {
    if (this.deferTimer !== null) return; // already waiting
    // eslint-disable-next-line no-console
    console.info(`[tour] "${id}" waiting for an open modal to close before starting`);
    const startedAt = Date.now();
    this.deferTimer = window.setInterval(() => {
      const reason = this.autoStartBlockReason(id);
      if (reason === BLOCKED_REASON && Date.now() - startedAt <= DEFER_TIMEOUT_MS) {
        return; // still blocked — keep waiting
      }
      this.clearDefer();
      if (reason === null) {
        // eslint-disable-next-line no-console
        console.info(`[tour] "${id}" auto-starting (modal closed)`);
        this.start(id);
      } else {
        const detail = reason === BLOCKED_REASON ? 'modal still open after timeout' : reason;
        // eslint-disable-next-line no-console
        console.info(`[tour] "${id}" did not auto-start: ${detail}`);
        // Terminal give-up (timed out / became ineligible while deferring): free the host's slot.
        this.onEvent?.('abort', id);
      }
    }, DEFER_POLL_MS);
  }

  private clearDefer(): void {
    if (this.deferTimer !== null) {
      window.clearInterval(this.deferTimer);
      this.deferTimer = null;
    }
  }

  /**
   * Start a tour. Respects `enabled` unless `force` is set (used by `?tour=` and
   * `restart`). Ignores schedule/seen — those only gate AUTO start.
   */
  start(id: string, options: { force?: boolean } = {}): void {
    const def = this.tours.get(id);
    if (!def) {
      // eslint-disable-next-line no-console
      console.warn(`[tour] unknown tour "${id}"`);
      this.onEvent?.('abort', id);
      return;
    }
    if (!options.force && !this.isEnabled(id)) {
      this.onEvent?.('abort', id);
      return;
    }

    this.clearDefer();
    this.stopActiveMonitor();
    this.active?.destroy();
    this.active = null;
    this.onEvent?.('start', id);

    this.activeId = id;
    this.active = createTourEngine(def, {
      onComplete: () => {
        this.seen.markSeen(id, def.version);
        this.onEvent?.('complete', id);
        this.onTourEnded();
      },
      onSkip: () => {
        this.seen.markSeen(id, def.version);
        this.onEvent?.('skip', id);
        this.onTourEnded();
      }
    });
    void this.active.start();
    this.startActiveMonitor(id);
  }

  private onTourEnded(): void {
    this.activeId = null;
    this.stopActiveMonitor();
  }

  /**
   * While a tour is on screen, watch for a blocking modal opening over it. If one does,
   * hide the tour (without recording it as "seen") and re-show it once the modal closes.
   * This makes the tour robust to modals that open before, during, or after it starts.
   */
  private startActiveMonitor(id: string): void {
    this.stopActiveMonitor();
    const check = (): void => {
      if (!this.active) {
        this.stopActiveMonitor();
        return;
      }
      if (this.blocked()) {
        // eslint-disable-next-line no-console
        console.info(`[tour] "${id}" paused for an open modal; will resume when it closes`);
        this.stopActiveMonitor();
        this.active.suspend();
        this.active = null;
        this.activeId = null;
        this.deferUntilUnblocked(id);
      }
    };
    // Instant reaction the moment a modal's element/classes change (kills the flash)...
    if (typeof MutationObserver !== 'undefined') {
      this.activeObserver = new MutationObserver(check);
      this.activeObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
    // ...plus a low-frequency backstop for anything the observer can't see.
    this.activeMonitor = window.setInterval(check, 250);
  }

  private stopActiveMonitor(): void {
    if (this.activeMonitor !== null) {
      window.clearInterval(this.activeMonitor);
      this.activeMonitor = null;
    }
    if (this.activeObserver) {
      this.activeObserver.disconnect();
      this.activeObserver = null;
    }
  }

  /** Clear "seen" and run the tour again from the top (used by a "Replay tour" action). */
  restart(id: string): void {
    this.seen.reset(id);
    this.start(id, { force: true });
  }

  /** Read `?tour=<id>` from the URL and force-launch it. Call once on app load. */
  handleQueryLaunch(search: string = window.location.search): void {
    const id = new URLSearchParams(search).get('tour');
    if (id) this.start(id, { force: true });
  }

  stop(): void {
    this.clearDefer();
    this.stopActiveMonitor();
    this.active?.destroy();
    this.active = null;
    this.activeId = null;
  }
}

/** App-wide singleton. */
export const tourManager = new TourManager();
